import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Pencil, Trash2, CheckCircle2, ChevronUp, ChevronDown, Hourglass } from "lucide-react";

/** Phase 36 — Sidebar list of pending enquiries. Collapsible.
 *  Props:
 *    enquiries, loading, onEdit(enquiry), onConfirm(enquiry), onChanged()
 */
function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function fmtTentative(iso) {
  if (!iso) return "No time set";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "No time set";
    return d.toLocaleString(undefined, { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "No time set";
  }
}

export default function PendingEnquiriesPanel({ enquiries, loading, onEdit, onConfirm, onChanged }) {
  const [collapsed, setCollapsed] = useState(false);
  const [discarding, setDiscarding] = useState(null); // enquiry object

  const sorted = [...(enquiries || [])].sort((a, b) => {
    // Items with tentative_start first, sorted ascending; then no-time items.
    if (a.tentative_start && b.tentative_start) {
      return new Date(a.tentative_start) - new Date(b.tentative_start);
    }
    if (a.tentative_start) return -1;
    if (b.tentative_start) return 1;
    return new Date(a.created_at) - new Date(b.created_at);
  });

  const handleDiscard = async () => {
    if (!discarding) return;
    try {
      await api.post(`/booking-enquiries/${discarding.id}/discard`);
      toast.success("Enquiry discarded");
      setDiscarding(null);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <aside className="bg-card border border-border rounded-lg shadow-sm" data-testid="pending-enquiries-panel">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between gap-3 p-3 border-b border-border hover:bg-secondary/40 transition-colors"
        data-testid="pending-enquiries-collapse-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Hourglass className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-extrabold uppercase tracking-wide">Pending enquiries</span>
          <span className="text-xs font-bold bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 border border-amber-200" data-testid="pending-enquiries-count">
            {(enquiries || []).length}
          </span>
        </div>
        {collapsed ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
      </button>

      {!collapsed && (
        <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto" data-testid="pending-enquiries-list">
          {loading ? (
            <div className="text-xs text-muted-foreground italic py-6 text-center">Loading…</div>
          ) : sorted.length === 0 ? (
            <div className="text-xs text-muted-foreground italic py-6 text-center" data-testid="pending-enquiries-empty">
              No pending enquiries — enquiries you take by phone will appear here.
            </div>
          ) : (
            sorted.map((e) => {
              const expiresIn = daysUntil(e.expires_at);
              return (
                <div
                  key={e.id}
                  className="border-2 border-amber-300 border-dashed bg-amber-50/40 rounded-md p-3 space-y-2"
                  data-testid={`pending-enquiry-${e.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">{e.customer_name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Phone className="h-3 w-3 shrink-0" />
                        <span className="truncate">{e.customer_phone}</span>
                      </div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-amber-800 bg-amber-200/60 border border-amber-300 rounded-sm px-1.5 py-0.5 shrink-0">
                      Pending
                    </span>
                  </div>
                  {e.address && (
                    <div className="text-xs text-muted-foreground flex items-start gap-1">
                      <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{e.address}</span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3 shrink-0" />
                    <span className="truncate font-mono">{fmtTentative(e.tentative_start)}</span>
                  </div>
                  {e.notes && (
                    <div className="text-xs text-foreground/80 bg-white/60 rounded p-1.5 line-clamp-3 border border-amber-200/60">
                      {e.notes}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-[10px] uppercase tracking-widest text-amber-800 font-bold">
                      Expires in {expiresIn ?? "—"} day{expiresIn === 1 ? "" : "s"}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button" variant="ghost" size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => onEdit && onEdit(e)}
                        data-testid={`pending-enquiry-edit-${e.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm"
                        className="h-7 px-2 text-xs text-rose-700 hover:bg-rose-50"
                        onClick={() => setDiscarding(e)}
                        data-testid={`pending-enquiry-discard-${e.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Discard
                      </Button>
                      <Button
                        type="button" size="sm"
                        className="h-7 px-2 text-xs btn-cta"
                        onClick={() => onConfirm && onConfirm(e)}
                        data-testid={`pending-enquiry-confirm-${e.id}`}
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <AlertDialog open={!!discarding} onOpenChange={(o) => !o && setDiscarding(null)}>
        <AlertDialogContent data-testid="enquiry-discard-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this enquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              The enquiry for <strong>{discarding?.customer_name}</strong> will be removed from the list.
              It stays archived for audit but won{"\u2019"}t be shown anywhere else.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="enquiry-discard-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscard} data-testid="enquiry-discard-confirm" className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500">
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
